import {Box, Flex, Text, Button, Image, IconButton} from '@chakra-ui/react';
import {
  IconPlayerPause,
  IconPlayerPlay,
  IconVolume,
  IconVolumeOff,
} from '@tabler/icons';
import * as React from 'react';
import {motion, useScroll, useTransform, useInView} from 'framer-motion';
import {useEffect, useRef, useState} from 'react';
import NextLink from 'next/link';
import {MotionBox} from './motionBox';
import {ScrollDownIndicator} from './scrollDownIndicator';
import {mixpanel} from '../../utils/mixpanel';
import {useCurrentAssetOverview} from '../../hooks/current-asset-overview';

interface BanksyHeroProps {
  minHeight?: number;
  mainBackdrop: string;
  midBackdrop: string;
  video: string;
  setShowProductFooter: (val: boolean) => void;
}
export const BanksyHero: React.FC<BanksyHeroProps> = ({
  minHeight = 100,
  mainBackdrop,
  midBackdrop,
  video,
  setShowProductFooter,
}) => {
  const asset = useCurrentAssetOverview('valentines.day.mascara');
  const {scrollY} = useScroll();
  const componentHeight = `${minHeight}vh`;

  const videoPlayerRef = useRef<HTMLVideoElement>(null);
  const [videoMuted, setVideoMuted] = useState(true);
  const [videoPlaying, setVideoPlaying] = useState(true);

  const firstSceneMarkerRef = useRef<HTMLDivElement>(null);
  const secondSceneMarkerRef = useRef<HTMLDivElement>(null);
  const thirdSceneSceneMarkerRef = useRef<HTMLDivElement>(null);

  const somethingInView = useInView(firstSceneMarkerRef);
  const videoInView = useInView(thirdSceneSceneMarkerRef);

  // Sizes
  const fullHeight = firstSceneMarkerRef?.current?.clientHeight || 0;
  const halfHeight = fullHeight / 2;
  const thirdHeight = fullHeight / 3;

  // Backdrop Animations and timings;
  const sceneOnePos = firstSceneMarkerRef?.current?.scrollTop || 0;
  const sceneOneStart = sceneOnePos;
  const sceneOneEnd = sceneOnePos + fullHeight;

  const sceneOneImageScale = useTransform(
    scrollY,
    [sceneOneStart, sceneOneEnd],
    [2.2, 1],
    {
      clamp: true,
    },
  );

  const introTextFade = useTransform(
    scrollY,
    [75, sceneOneEnd - halfHeight],
    [1, 0],
    {
      clamp: true,
    },
  );

  const detailFade = useTransform(
    scrollY,
    [sceneOneStart, sceneOneEnd, sceneOneEnd + fullHeight],
    [0, 1, 0],
    {
      clamp: true,
    },
  );

  const detailFadeSecond = useTransform(
    scrollY,
    [sceneOneStart + thirdHeight, sceneOneEnd, sceneOneEnd + fullHeight],
    [0, 1, 0],
    {
      clamp: true,
    },
  );

  const sceneTwoPos = secondSceneMarkerRef?.current?.offsetTop || 0;
  const sceneTwoStart = sceneTwoPos - fullHeight;
  const sceneTwoEnd = sceneTwoPos;

  const sceneTwoImageScale = useTransform(
    scrollY,
    [sceneTwoStart, sceneTwoEnd],
    [2.2, 1],
    {
      clamp: true,
    },
  );

  const sceneTwoImageOpacity = useTransform(
    scrollY,
    [sceneTwoStart, sceneTwoEnd],
    [0, 1],
    {
      clamp: true,
    },
  );

  const aboutBanksyFade = useTransform(
    scrollY,
    [sceneTwoStart, sceneTwoEnd, sceneTwoEnd + halfHeight],
    [0, 1, 0],
    {
      clamp: true,
    },
  );

  const sceneThreePos = thirdSceneSceneMarkerRef?.current?.offsetTop || 0;
  const sceneThreeStart = sceneThreePos - fullHeight;
  const sceneThreeEnd = sceneThreePos + fullHeight;

  const sceneThreeImageScale = useTransform(
    scrollY,
    [sceneThreeStart, sceneThreeEnd],
    [2.2, 1],
    {
      clamp: true,
    },
  );

  const sceneThreeImageOpacity = useTransform(
    scrollY,
    [sceneThreeStart, sceneThreeEnd],
    [0, 1],
    {
      clamp: true,
    },
  );

  const SceneOneArrowPos = useTransform(scrollY, [0, 200], [0, 300], {
    clamp: true,
  });

  const SceneArrowOpacity = useTransform(scrollY, [0, 200], [1, 0], {
    clamp: true,
  });

  useEffect(() => {
    if (!videoPlayerRef?.current) return;
    if (!videoPlaying) return; // If the user has paused the video we want respect their decision
    if (videoInView) {
      videoPlayerRef.current.play();
    } else {
      videoPlayerRef.current.pause();
    }
    setShowProductFooter(videoInView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoInView]);

  return (
    <Box id="banksyHero" pos="relative" minH={componentHeight} bg="#000A22">
      <Box
        id="banksyBackdrop"
        zIndex={1}
        pos="absolute"
        top={0}
        left={0}
        w="100%"
        height="100%"
      >
        <Box pos="sticky" top={0} overflow="hidden" h={componentHeight}>
          {/* Opening Bansky Image */}
          <MotionBox
            initial={{
              scale: 2.2,
            }}
            pos="absolute"
            top={0}
            left={0}
            w="100%"
            h={componentHeight}
            zIndex={1}
            style={{
              scale: sceneOneImageScale,
            }}
          >
            <Box
              height={componentHeight}
              w="100%"
              pos="relative"
              overflow="hidden"
            >
              <Image
                src={mainBackdrop}
                alt="awesome"
                pos="absolute"
                top={0}
                left={0}
                w="100%"
                height="100%"
                objectFit="cover"
                objectPosition="top center"
                zIndex={1}
              />
            </Box>
          </MotionBox>

          {/* Banksy hoodie image */}
          <MotionBox
            initial={{
              scale: 2.2,
              opacity: 0,
            }}
            pos="absolute"
            top={0}
            left={0}
            w="100%"
            h={componentHeight}
            zIndex={2}
            style={{
              scale: sceneTwoImageScale,
              opacity: sceneTwoImageOpacity,
            }}
          >
            <Box
              height={componentHeight}
              w="100%"
              pos="relative"
              overflow="hidden"
            >
              <Image
                src={midBackdrop}
                alt="awesome"
                pos="absolute"
                top={0}
                left={0}
                w="100%"
                height="100%"
                objectFit="cover"
                zIndex={2}
              />
            </Box>
          </MotionBox>

          {/* Banksy video */}
          <MotionBox
            initial={{
              scale: 2.2,
              opacity: 0,
            }}
            pos="absolute"
            top={0}
            left={0}
            w="100%"
            h={componentHeight}
            zIndex={3}
            opacity={0}
            style={{
              scale: sceneThreeImageScale,
              opacity: sceneThreeImageOpacity,
            }}
          >
            <Box
              height={componentHeight}
              w="100%"
              pos="relative"
              overflow="hidden"
              sx={{
                video: {
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                },
              }}
            >
              <video ref={videoPlayerRef} muted loop controls={false}>
                <source src={video} type="video/mp4" />
              </video>
            </Box>
          </MotionBox>
        </Box>
      </Box>

      {/* Main Content Body */}
      <Box position="relative" zIndex={2}>
        {/* Intro box */}
        <motion.div
          style={{
            position: 'relative',
            overflow: 'hidden',
          }}
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          transition={{
            duration: 1.5,
            delay: 0.3,
          }}
        >
          <Flex
            minH={componentHeight}
            justifyContent="center"
            alignItems="center"
          >
            <Box w="100%" maxW="760px" p="0 25px">
              <MotionBox style={{opacity: introTextFade}}>
                <Text fontSize="33px" textAlign="center" fontWeight="600">
                  Showpiece is pleased to announce that another iconic treasure
                  has been added to the collection, and this really is the
                  biggest yet (literally).
                </Text>
              </MotionBox>
            </Box>
          </Flex>
          <MotionBox
            style={{
              y: SceneOneArrowPos,
              opacity: SceneArrowOpacity,
            }}
            pos="absolute"
            bottom="100px"
            left="calc(50% - 25px)"
            transform="translateY(-50%)"
          >
            <ScrollDownIndicator />
          </MotionBox>
        </motion.div>

        {/* Detail/Sale box */}
        <Flex
          ref={firstSceneMarkerRef}
          minH={componentHeight}
          justifyContent="center"
          alignItems="center"
        >
          <Box w="100%" maxW="630px" padding="30px" position="relative">
            <MotionBox
              position="absolute"
              top={0}
              left={0}
              w="100%"
              h="100%"
              borderRadius="10px"
              background="rgba(255, 255, 255, .9)"
              zIndex={0}
              style={{
                opacity: detailFade,
              }}
            />
            <MotionBox
              pos="relative"
              zIndex={1}
              style={{
                opacity: detailFadeSecond,
              }}
            >
              <Text fontSize="40px" lineHeight="1.1" fontWeight="600" mb="25px">
                Banksy: Valentine’s Day Mascara
              </Text>
              <Text fontSize="28px" lineHeight="1.1" fontWeight="600" mb="10px">
                Valuation | £6,000,000
              </Text>
              <Text mb={2} fontSize="18px">
                In the heart of the seaside town of Margate, a graffiti mural
                was discovered on Valentine’s Day, 2023. Depicting an archetypal
                housewife closing a fridge on her victim, the piece would appear
                to be a comedic slapstick scene at first glance. However, upon
                closer inspection, a black eye, missing tooth, beer bottle, and
                frying pan suggested a darker tone. This kind of commentary
                could only come from one artist. Banksy.
              </Text>
              <Box mt="20px">
                {asset.status === 'COMING_SOON' ? (
                  <NextLink href="/banksy-waitlist">
                    <Button
                      colorScheme="aqua"
                      width="215px"
                      onClick={() => {
                        mixpanel.track(
                          'Banksy product page sign up to waitlist click',
                        );
                      }}
                    >
                      Sign up to waitlist
                    </Button>
                  </NextLink>
                ) : (
                  <NextLink href="/own-a-piece/valentines-day-mascara">
                    <Button
                      colorScheme="aqua"
                      width="215px"
                      onClick={() => {
                        mixpanel.track('Banksy product page buy click');
                      }}
                    >
                      Buy
                    </Button>
                  </NextLink>
                )}
              </Box>
            </MotionBox>
          </Box>
        </Flex>

        {/* About Banksy */}
        <Flex
          ref={secondSceneMarkerRef}
          minH={componentHeight}
          justifyContent="center"
          alignItems="flex-end"
        >
          <MotionBox
            w="100%"
            maxW="760px"
            p="0 25px"
            color="white"
            pb="10vh"
            style={{opacity: aboutBanksyFade}}
          >
            <Text fontSize="33px" fontWeight="600" mb="15px">
              Banksy
            </Text>
            <Text fontSize="18px">
              An enigma of international art, Banksy is the alias of an unknown
              British graffiti artist thought to have emerged from Bristol
              Underground Scene in the 1990s. Since then, Banksy’s work has
              appeared worldwide, from the streets of New York to the West Bank.
              His iconic stencil style often depicts dark humour, satire and
              sociopolitical commentary themes. Except for a limited number of
              works released for sale by Banksy&apos;s representative agency,
              ‘Pest Control’, most of the artists’ work is created on publicly
              visible surfaces.
            </Text>
          </MotionBox>
          <Box minH="30vh" />
        </Flex>
        {/* spacer */}
        <Box minH="30vh" />
        <Box ref={thirdSceneSceneMarkerRef}>
          <Box minH={componentHeight} />
          <Box minH={componentHeight} />
        </Box>
      </Box>

      {/* Hero Fixed footer */}
      <Box
        position="sticky"
        bottom={0}
        left={0}
        width="100%"
        overflow="hidden"
        zIndex={somethingInView ? 10 : 3}
      >
        <MotionBox
          background="black"
          variants={{
            initial: {y: '100%'},
            inView: {y: 0},
          }}
          animate={somethingInView ? 'inView' : 'initial'}
        >
          <Box maxW="1040px" margin="0 auto" px="10px">
            <Box pos="relative" overflowX="scroll" width="100%">
              <Flex
                color="gray.50"
                height="75px"
                flexDirection="row"
                flexWrap="nowrap"
                alignItems="center"
                justifyContent="flex-start"
              >
                <Text textStyle="h4" fontSize="20px" whiteSpace="nowrap">
                  As featured in
                </Text>
                {[
                  {
                    icon: 'sky-news.png',
                    link: 'https://news.sky.com/story/rarest-stamp-in-the-world-dubbed-mona-lisa-of-stamps-returns-to-uk-after-143-years-12356170',
                  },
                  {
                    icon: 'bbc.png',
                    link: 'https://www.bbc.co.uk/news/uk-57845086',
                  },
                  {
                    icon: 'independent.png',
                    link: 'https://www.independent.co.uk/money/stakes-in-rare-king-edward-viii-penny-snapped-up-b2031172.html',
                  },
                  {
                    icon: 'ft.png',
                    link: 'https://www.ft.com/content/77790ee0-ce9e-4f77-995b-76e7e4f92ca2',
                  },
                  {
                    icon: 'mail.png',
                    link: 'https://www.dailymail.co.uk/femail/article-11411975/Own-share-edition-Charles-Darwins-Origin-Species.html',
                  },
                ].map((logo) => {
                  return (
                    <NextLink href={logo.link} target="_blank">
                      <Image
                        src={`/assets/banksy/${logo.icon}`}
                        alt="publication logo"
                        ml="25px"
                        cursor="pointer"
                      />
                    </NextLink>
                  );
                })}
              </Flex>
            </Box>
          </Box>
        </MotionBox>
      </Box>
      <Box
        position="sticky"
        bottom="62px"
        left={0}
        width="100%"
        overflow="hidden"
        zIndex={3}
      >
        <motion.div
          style={{padding: '10px'}}
          variants={{
            initial: {y: '100%'},
            inView: {y: 0},
          }}
          animate={videoInView ? 'inView' : 'initial'}
        >
          <IconButton
            aria-label="mute sound toggle"
            isRound
            icon={videoMuted ? <IconVolumeOff /> : <IconVolume />}
            px="12px"
            onClick={() => {
              if (!videoPlayerRef?.current) return;
              if (videoMuted) {
                setVideoMuted(false);
                // eslint-disable-next-line no-param-reassign
                videoPlayerRef.current.muted = false;
              } else {
                // eslint-disable-next-line no-param-reassign
                videoPlayerRef.current.muted = true;
                setVideoMuted(true);
              }
              mixpanel.track('Banksy product page video mute button clicked');
            }}
          />

          <IconButton
            ml="10px"
            aria-label="mute sound toggle"
            isRound
            icon={videoPlaying ? <IconPlayerPause /> : <IconPlayerPlay />}
            px="12px"
            onClick={() => {
              if (!videoPlayerRef?.current) return;
              if (videoPlaying) {
                setVideoPlaying(false);
                videoPlayerRef.current.pause();
              } else {
                videoPlayerRef.current.play();
                setVideoPlaying(true);
              }
              mixpanel.track(
                `Banksy product page ${
                  videoPlaying ? 'pause' : 'play'
                } button clicked`,
              );
            }}
          />
        </motion.div>
      </Box>
    </Box>
  );
};
